'use client';

import { getCmsLocalizedHref } from '@/cms/chrome/localized-href';
import type {
  CmsAccountSettingsDialogState,
  CmsAccountSettingsSection,
} from '@/cms/components/chrome/cms-account-settings-state';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { getNavMinWidthMediaQuery } from '@/components/layout/navigation/header/header-breakpoint';
import { UserAvatar } from '@/components/layout/user/user-avatar';
import { authClient } from '@/lib/auth/auth-client';
import {
  beginAuthSessionTransition,
  cancelAuthSessionTransition,
} from '@/lib/auth/auth-session-observer';
import { clearAuthScopedQueryCache } from '@/lib/query/auth-query-cache';
import { toast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { Routes } from '@/routes';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

type CmsAccountMenuUser = {
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

export type { CmsAccountSettingsSection } from './cms-account-settings-state';

type CmsAccountMenuSettingsItem = {
  kind: 'settings';
  section: CmsAccountSettingsSection;
  label: string;
  icon: string;
};

type CmsAccountMenuActionItem = {
  kind: 'action';
  action: 'logout';
  label: string;
  icon: string;
};

type CmsAccountMenuItem = CmsAccountMenuSettingsItem | CmsAccountMenuActionItem;

type CmsAccountMenuSection = {
  title: string;
  items: CmsAccountMenuItem[];
};

export type CmsAccountLocalCopy = {
  billingLabel: string;
  closeMenuLabel: string;
  creditsLabel: string;
  freePlanLabel: string;
  getMoreLabel: string;
  logoutFailedMessage: string;
  logoutLabel: string;
  openMenuLabel: string;
  paidPlanLabel: string;
  profileFallbackLabel: string;
  profileLabel: string;
  securityLabel: string;
  settingsSectionTitle: string;
  upgradeLabel: string;
};

export function useCmsAccountLocalCopy(): CmsAccountLocalCopy {
  const t = useTranslations('Common');

  return useMemo(
    () => ({
      billingLabel: t('accountMenu.billing'),
      closeMenuLabel: t('accountMenu.close'),
      creditsLabel: t('accountMenu.credits'),
      freePlanLabel: t('accountMenu.freePlan'),
      getMoreLabel: t('accountMenu.getMore'),
      logoutFailedMessage: t('logoutFailed'),
      logoutLabel: t('logout'),
      openMenuLabel: t('accountMenu.open'),
      paidPlanLabel: t('accountMenu.paidPlan'),
      profileFallbackLabel: t('accountMenu.profileFallback'),
      profileLabel: t('accountMenu.profile'),
      securityLabel: t('accountMenu.security'),
      settingsSectionTitle: t('accountMenu.settings'),
      upgradeLabel: t('accountMenu.upgrade'),
    }),
    [t]
  );
}

type CmsAccountMenuContentClassNames = {
  panel: string;
  user: string;
  userRow: string;
  userAvatar: string;
  userIdentity: string;
  userMeta: string;
  userName: string;
  userEmail: string;
  separator: string;
  sectionTitle: string;
  item: string;
  itemIcon: string;
};

const headerAccountMenuContentClassNames: CmsAccountMenuContentClassNames = {
  panel:
    'rounded-panel border border-border/70 bg-[var(--cms-workspace-floating-surface-background)] p-2 text-on-surface shadow-lg',
  user: 'px-3 py-2',
  userRow: 'flex items-start justify-between gap-3',
  userAvatar: 'size-8 shrink-0 border border-border/60 bg-muted',
  userIdentity: 'min-w-0 flex-1 space-y-0.5',
  userMeta: 'shrink-0',
  userName: 'text-sm font-semibold text-on-surface',
  userEmail: 'text-xs font-normal text-muted-foreground',
  separator: 'my-1 h-px bg-white/10',
  sectionTitle: 'cms-caption px-3 pb-1 pt-2 text-muted-foreground',
  item: 'flex w-full items-center gap-3 rounded-floating px-3 py-2.5 text-left text-sm font-medium text-on-surface transition-colors hover:bg-white/5',
  itemIcon: 'size-4 shrink-0 text-muted-foreground',
};

export function buildCmsAccountMenuSections(
  copy: CmsAccountLocalCopy
): CmsAccountMenuSection[] {
  return [
    {
      title: '',
      items: [
        {
          kind: 'settings',
          section: 'profile',
          label: copy.profileLabel,
          icon: 'circle-user-round',
        },
        {
          kind: 'action',
          action: 'logout',
          label: copy.logoutLabel,
          icon: 'log-out',
        },
      ],
    },
  ];
}

export function useCmsAccountSignOut(
  locale: string,
  copy: CmsAccountLocalCopy
) {
  const queryClient = useQueryClient();
  const router = useRouter();

  const handleSignOut = async () => {
    const transition = beginAuthSessionTransition(
      'sign-out',
      'cms-account-menu'
    );
    let reportedError = false;
    const reportFailure = () => {
      cancelAuthSessionTransition(transition);
      if (!reportedError) {
        reportedError = true;
        toast.error(copy.logoutFailedMessage);
      }
    };

    try {
      await authClient.signOut({
        fetchOptions: {
          onSuccess: () => {
            clearAuthScopedQueryCache(queryClient);
            router.replace(getCmsLocalizedHref(locale, Routes.Root));
          },
          onError: () => {
            reportFailure();
          },
        },
      });
    } catch {
      reportFailure();
    }
  };

  return {
    handleSignOut,
  };
}

export function CmsAccountMenuContent({
  classNames = headerAccountMenuContentClassNames,
  onLogout,
  onNavigate,
  onOpenSettings,
  sections,
  showUserAvatar = false,
  showUserSummary = true,
  user,
  userMeta,
  profileFallbackLabel,
}: {
  classNames?: CmsAccountMenuContentClassNames;
  onLogout: () => void;
  onNavigate?: () => void;
  onOpenSettings: (section: CmsAccountSettingsSection) => void;
  sections: CmsAccountMenuSection[];
  showUserAvatar?: boolean;
  showUserSummary?: boolean;
  user: CmsAccountMenuUser;
  userMeta?: ReactNode;
  profileFallbackLabel: string;
}) {
  return (
    <div className={classNames.panel}>
      {showUserSummary ? (
        <>
          <div className={classNames.user}>
            <div className={classNames.userRow}>
              {showUserAvatar ? (
                <UserAvatar
                  name={user.name ?? profileFallbackLabel}
                  image={user.image}
                  className={classNames.userAvatar}
                />
              ) : null}
              <div className={classNames.userIdentity}>
                <div className={classNames.userName}>
                  {user.name ?? profileFallbackLabel}
                </div>
                {user.email ? (
                  <div className={classNames.userEmail}>{user.email}</div>
                ) : null}
              </div>
              {userMeta ? (
                <div className={classNames.userMeta}>{userMeta}</div>
              ) : null}
            </div>
          </div>
          <div className={classNames.separator} />
        </>
      ) : null}
      {sections.map((section, sectionIndex) => (
        <div key={section.title || `section-${sectionIndex}`}>
          {section.title ? (
            <div className={classNames.sectionTitle}>{section.title}</div>
          ) : null}
          {section.items.map((item) =>
            item.kind === 'settings' ? (
              <button
                key={`${section.title}-${item.section}`}
                type="button"
                className={classNames.item}
                onClick={() => {
                  onNavigate?.();
                  onOpenSettings(item.section);
                }}
              >
                {renderAppIcon({
                  name: item.icon,
                  className: classNames.itemIcon,
                })}
                <span>{item.label}</span>
              </button>
            ) : (
              <button
                key={`${section.title}-${item.action}`}
                type="button"
                className={classNames.item}
                onClick={() => {
                  onNavigate?.();
                  onLogout();
                }}
              >
                {renderAppIcon({
                  name: item.icon,
                  className: classNames.itemIcon,
                })}
                <span>{item.label}</span>
              </button>
            )
          )}
          {sectionIndex < sections.length - 1 ? (
            <div className={classNames.separator} />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function CmsAccountMenuSectionList({
  onLogout,
  onOpenSettings,
  sections,
  onNavigate,
}: {
  onLogout: () => void;
  onOpenSettings: (section: CmsAccountSettingsSection) => void;
  sections: CmsAccountMenuSection[];
  onNavigate?: () => void;
}) {
  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <section key={section.title} className="space-y-2">
          {section.title ? (
            <div className="cms-caption px-1 text-muted-foreground">
              {section.title}
            </div>
          ) : null}
          <div className="space-y-1">
            {section.items.map((item) =>
              item.kind === 'settings' ? (
                <button
                  key={`${section.title}-${item.section}`}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-floating px-3 py-2.5 text-left text-sm font-medium text-on-surface transition-colors hover:bg-white/5"
                  onClick={() => {
                    onNavigate?.();
                    onOpenSettings(item.section);
                  }}
                >
                  {renderAppIcon({
                    name: item.icon,
                    className: 'size-4 shrink-0 text-muted-foreground',
                  })}
                  <span>{item.label}</span>
                </button>
              ) : (
                <button
                  key={`${section.title}-${item.action}`}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-floating px-3 py-2.5 text-sm font-medium text-on-surface transition-colors hover:bg-white/5"
                  onClick={() => {
                    onNavigate?.();
                    onLogout();
                  }}
                >
                  {renderAppIcon({
                    name: item.icon,
                    className: 'size-4 shrink-0 text-muted-foreground',
                  })}
                  <span>{item.label}</span>
                </button>
              )
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

export function CmsAccountMenu({
  locale,
  settingsDialog,
  user,
}: {
  locale: string;
  settingsDialog: CmsAccountSettingsDialogState;
  user: CmsAccountMenuUser;
}) {
  const copy = useCmsAccountLocalCopy();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sections = useMemo(() => buildCmsAccountMenuSections(copy), [copy]);
  const { handleSignOut } = useCmsAccountSignOut(locale, copy);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(getNavMinWidthMediaQuery());
    const handleDesktopChange = () => {
      if (mediaQueryList.matches) {
        setMobileOpen(false);
      }
    };

    handleDesktopChange();
    mediaQueryList.addEventListener('change', handleDesktopChange);
    return () =>
      mediaQueryList.removeEventListener('change', handleDesktopChange);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = mobileOpen ? 'hidden' : previousOverflow;

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  return (
    <>
      <div className="hidden md:flex">
        <div className="group relative inline-flex">
          <button
            type="button"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-pill border border-border/60 bg-background/80 transition-transform duration-200 hover:-translate-y-px"
            aria-label={user.name ?? copy.profileFallbackLabel}
            title={user.name ?? copy.profileFallbackLabel}
          >
            <UserAvatar
              name={user.name ?? copy.profileFallbackLabel}
              image={user.image}
              className="size-8 border-0 bg-muted"
            />
          </button>
          <div className="invisible absolute right-0 top-full z-[60] w-[22rem] pt-3 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
            <CmsAccountMenuContent
              onLogout={() => {
                void handleSignOut();
              }}
              onOpenSettings={settingsDialog.openSettings}
              sections={sections}
              user={user}
              profileFallbackLabel={copy.profileFallbackLabel}
            />
          </div>
        </div>
      </div>

      <div className="md:hidden">
        <button
          type="button"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-pill border border-border/60 bg-background/80 transition-transform duration-200"
          aria-label={mobileOpen ? copy.closeMenuLabel : copy.openMenuLabel}
          onClick={() => setMobileOpen((current) => !current)}
        >
          <UserAvatar
            name={user.name ?? copy.profileFallbackLabel}
            image={user.image}
            className="size-8 border-0 bg-muted"
          />
        </button>

        <div
          className={cn(
            'fixed inset-x-0 bottom-0 top-[52px] z-[56] overflow-x-clip overflow-y-auto border-t border-border/40 bg-background px-6 pb-10 pt-4 text-on-surface transition-all duration-300 ease-out md:hidden',
            mobileOpen
              ? 'visible opacity-100'
              : 'invisible pointer-events-none opacity-0'
          )}
          aria-hidden={!mobileOpen}
        >
          <div className="space-y-5">
            <div className="flex items-start gap-3 border-b border-border/40 pb-4">
              <button
                type="button"
                className="inline-flex shrink-0 rounded-pill"
                onClick={() => {
                  setMobileOpen(false);
                  settingsDialog.openSettings('profile');
                }}
              >
                <UserAvatar
                  name={user.name ?? copy.profileFallbackLabel}
                  image={user.image}
                  className="size-11 border border-border/60 bg-muted"
                />
              </button>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-on-surface">
                  {user.name ?? copy.profileFallbackLabel}
                </div>
                {user.email ? (
                  <div className="truncate pt-0.5 text-xs text-muted-foreground">
                    {user.email}
                  </div>
                ) : null}
              </div>
            </div>

            <CmsAccountMenuSectionList
              onLogout={() => {
                void handleSignOut();
              }}
              onOpenSettings={settingsDialog.openSettings}
              sections={sections}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      </div>
    </>
  );
}
